import { createWork4MeScript } from '@/api/scripts-api';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import type { JsonValue } from '@vivport/backend/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { parseJsonText, Work4MeScriptForm } from './Work4MeScriptForm';

const DEFAULT_BODY = 'function run(input) {\n  return input;\n}\n';

interface Work4MeScriptCreateProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (scriptId: number) => void;
}

export function Work4MeScriptCreate({ open, onOpenChange, onCreated }: Work4MeScriptCreateProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState(DEFAULT_BODY);
  const [inputExampleText, setInputExampleText] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);

  function reset() {
    setName('');
    setDescription('');
    setBody(DEFAULT_BODY);
    setInputExampleText('');
    setIsEnabled(true);
  }

  const createMutation = useMutation({
    mutationFn: () => {
      const parsed = parseJsonText(inputExampleText);
      if (!parsed.ok) {
        throw new Error(`Input example is not valid JSON: ${parsed.message}`);
      }
      return createWork4MeScript({
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        body,
        inputExample: parsed.value as JsonValue,
        isEnabled,
      });
    },
    onSuccess: async (created) => {
      toast.success(`Created "${created.name}".`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['work4me-scripts'] }),
        queryClient.invalidateQueries({ queryKey: ['work4me-script', created.id] }),
      ]);
      onCreated?.(created.id);
      onOpenChange(false);
      reset();
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create script.');
    },
  });

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) {
          reset();
        }
      }}
    >
      <SheetContent className="w-screen sm:w-[720px] flex flex-col px-0">
        <SheetHeader className="px-4">
          <SheetTitle>Create Script</SheetTitle>
          <SheetDescription>A deterministic calculation Neo and agents can run: JSON in, JSON out.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <Work4MeScriptForm
            name={name}
            description={description}
            body={body}
            inputExampleText={inputExampleText}
            isEnabled={isEnabled}
            isDirty={true}
            isSaving={createMutation.isPending}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onBodyChange={setBody}
            onInputExampleTextChange={setInputExampleText}
            onIsEnabledChange={setIsEnabled}
            onSave={() => createMutation.mutate()}
            submitLabel="Create Script"
            submittingLabel="Creating..."
            editableHint="New script configuration is saved when created."
          />
        </div>

        <SheetFooter className="px-4">
          <div className="w-full">
            <Separator className="mb-4" />
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
              Close
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
